import { Fragment, ReactNode, useContext } from 'react';

import { Collapse, List } from '@atom/mui';
import { SchemaTree, SchemaTreeElementGroup } from '@atom/types/schema';

import { groupKey, isSelected, pathKey } from './addElementsModalUtilities';
import ElementSelectionContext from './ElementSelectionContext';
import ElementSelectionGroupRow from './ElementSelectionGroupRow';
import ElementSelectionRow from './ElementSelectionRow';

type ElementSelectionBranchProps = {
  elements: SchemaTree[];
  groups?: SchemaTreeElementGroup[];
  parentPath: string[];
  depth: number;
  disabled?: boolean;
};

const ElementSelectionBranch = ({
  elements,
  groups = [],
  parentPath,
  depth,
  disabled = false,
}: ElementSelectionBranchProps) => {
  const { selection, expanded, onToggleExpanded } = useContext(
    ElementSelectionContext,
  );

  const renderElement = (element: SchemaTree, rowDepth: number): ReactNode => {
    const path = [...parentPath, element.id];
    const children = element.elements ?? [];

    return (
      <Fragment key={element.id}>
        <ElementSelectionRow
          element={element}
          path={path}
          depth={rowDepth}
          disabled={disabled}
        />
        {children.length > 0 && (
          <Collapse
            in={expanded.has(pathKey(path))}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              <ElementSelectionBranch
                elements={children}
                parentPath={path}
                depth={rowDepth + 1}
                disabled={disabled || !isSelected(selection, path)}
              />
            </List>
          </Collapse>
        )}
      </Fragment>
    );
  };

  return (
    <>
      {elements.map(element => renderElement(element, depth))}
      {groups.map(group => {
        const key = groupKey(group);

        return (
          <Fragment key={group.id}>
            <ElementSelectionGroupRow
              group={group}
              depth={depth}
              isExpanded={expanded.has(key)}
              toggleKey={key}
              onToggle={onToggleExpanded}
            />
            <Collapse in={expanded.has(key)} timeout="auto" unmountOnExit>
              <List disablePadding>
                {(group.elements ?? []).map(element =>
                  renderElement(element, depth + 1),
                )}
              </List>
            </Collapse>
          </Fragment>
        );
      })}
    </>
  );
};

export default ElementSelectionBranch;
